import { SxProps, Theme } from '@mui/material';
import type { TaskCreatorFormValues } from './HomePageTaskCreatorUtils';

export type HomePageTaskCreatorProps = {
  onTaskCreated: () => void;
};

export type TaskCreatorType = { id: number; name: string };

export const TASK_TYPE_NAME = 'Task';

export const TASK_FROM_TEMPLATE_TYPE_NAME = 'Task from template';

export const REMINDER_TYPE_NAME = 'Reminder';

export const MESSAGE_TYPE_NAME = 'Message';

export const TAX_PAYMENT_TYPE_NAME = 'Tax payment';

export const NOTE_TYPE_NAME = 'Note';

export const NOTE_TYPE_ID = -1;

export const isTypeName = (name: string | null | undefined, expected: string) =>
  name?.trim().toLowerCase() === expected.toLowerCase();

export type DropdownOption = {
  label: string;
  value: number;
};

export type StringDropdownOption = {
  label: string;
  value: string;
};

export const NOTE_RELATED_TO_OPTIONS: DropdownOption[] = [
  { label: 'Income tax', value: 1 },
  { label: 'Payroll', value: 2 },
  { label: 'Sales tax', value: 3 },
  { label: 'Other', value: 4 },
];

export type TaskFieldName = keyof TaskCreatorFormValues;

export const TASK_FIELD_LABELS: Record<TaskFieldName, string> = {
  type: 'Type',
  assignedTo: 'Assigned to',
  client: 'Client',
  description: 'Description',
  template: 'Template',
  note: 'Note',
  subType: 'Sub-type',
  detail: 'Detail',
  related: 'Related',
  dueDate: 'Due date',
  showUntilDone: 'Show until done',
  isBillable: 'Billable',
  amount: 'Amount',
  callbackNumber: 'Callback number',
  isUrgent: 'Mark urgent',
  taxYear: 'Tax year',
  taxPaymentFiling: 'Tax payment filing',
  taxPaymentTemplate: 'Tax payment template',
  taxPaymentType: 'Tax payment type',
  isPaid: 'Paid',
  estimatedAmount: 'Estimated',
  datePaid: 'Date paid',
  paymentMethod: 'Payment method',
  overpaymentAmount: 'Overpayment amount',
  overpaymentSetting: 'Overpayment setting',
  important: 'Important',
  noteRelatedTo: 'Related to',
  specificTemplate: 'Specific template',
};

export const ContainerSx = (isExpanded: boolean): SxProps<Theme> => ({
  flexShrink: 0,
  backgroundColor: 'common.white',
  border: '1px solid',
  borderColor: 'customColors.gray.200',
  borderRadius: '8px',
  padding: !isExpanded ? '5px 20px' : '5px 20px 10px 20px',
  transition: 'padding 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
});

export const FieldsCollapseSx: SxProps<Theme> = {
  marginTop: '12px',
};

export const SummaryErrorsSx: SxProps = {
  maxWidth: 'calc(100% - 16px)',
  marginTop: '-44px',
};

export const HeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  cursor: 'pointer',
};

export const HeaderTitleSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const TitleSx: SxProps<Theme> = {
  fontSize: '20px',
  lineHeight: '28px',
  fontWeight: 500,
  color: 'customColors.gray.950',
};

export const TypeTriggerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: 0,
  minWidth: 'auto',
  fontSize: '20px',
  lineHeight: '28px',
  fontWeight: 500,
  color: 'customColors.blue.500',
  textTransform: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'customColors.blue.600',
  },
  '&.Mui-disabled': {
    color: 'customColors.blue.500',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
};

export const TypeMenuPaperSx: SxProps<Theme> = {
  marginTop: '4px',
  minWidth: '180px',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'customColors.gray.200',
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
};

export const TypeMenuItemSx: SxProps<Theme> = {
  fontSize: '14px',
  lineHeight: '20px',
  color: 'customColors.gray.950',
  '&.Mui-selected': {
    backgroundColor: 'customColors.blue.100',
  },
};

export const HeaderActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

export const ExpandToggleSx = (isExpanded: boolean): SxProps<Theme> => ({
  color: 'customColors.gray.950',
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
});

const ActionButtonSx: SxProps<Theme> = {
  minWidth: '90px',
  fontSize: '14px',
  padding: '4px 12px 4px 13px',
  lineHeight: '24px',
};

export const ClearButtonSx: SxProps<Theme> = {
  ...ActionButtonSx,
  color: 'customColors.gray.950',
  borderColor: 'customColors.gray.200',
};

export const SaveButtonSx: SxProps<Theme> = ActionButtonSx;

export const FieldsGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '16px 24px',
  alignItems: 'start',
  marginTop: { xs: '48px', lg: '44px' },
};

export const DescriptionRowSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px 24px',
};

export const ThreeColumnRowSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '16px 24px',
  alignItems: 'start',
};

export const FourColumnRowSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '16px 24px',
  alignItems: 'start',
};

export const FullWidthRowSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
};

export const CheckboxRowSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'flex',
  alignItems: 'center',
};

export const EmptyStateSx: SxProps<Theme> = {
  fontSize: '14px',
  lineHeight: '20px',
  color: 'customColors.gray.500',
};

export const EntityMismatchSx: SxProps<Theme> = {
  fontSize: '12px',
  lineHeight: '16px',
  color: 'customColors.red.500',
};
